import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Search, Plus, X, ChevronLeft, ChevronRight, Download, Loader2, HelpCircle, Lock, ListChecks } from "lucide-react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { FilterDropdown } from "../components/ui/filter-dropdown";
import { DataTable, DataTableColumn } from "../components/ui/data-table";
import { EmptyState } from "../components/ui/empty-state";
import {
  StatusBadge, PriorityBadge, BemiddelingBadge,
  STATUS_LABELS, PRIORITY_LABELS, BEMIDDELING_LABELS, BEMIDDELING_OPTIONS,
  statusPillKind, priorityPillKind, bemiddelingPillKind,
} from "../components/VacatureBadges";
import { ChipSelect, ChipSelectOption } from "../components/vacatures/ChipSelect";
import { toast } from "../lib/toast";
import { useVacatures, useBranches, usePartners } from "../hooks/queries";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import NieuweVacatureModal from "../components/VacatureModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../hooks/useAuth";
import { useSettingsDialog } from "../components/SettingsDialog";
import { exportVacatures, listAllVacatures, updateVacature, Vacature } from "../api/api";
import { isPracticeId } from "../lib/practice";
import PartnersOverview from "../components/vacatures/PartnersOverview";
import BulkEditModal, { BulkSelectBar, BulkDeleteModal } from "../components/vacatures/BulkEdit";

const PRIO_ORDER: Record<string, number> = { hoog: 0, normaal: 1, laag: 2 };
const PAGE_SIZE = 50;

const STATUS_FILTER = [
  { value: "alle",     label: "Alle statussen" },
  { value: "open",     label: "Open" },
  { value: "hold",     label: "On hold" },
  { value: "gesloten", label: "Gesloten" },
];

const PRIO_FILTER = [
  { value: "alle",    label: "Alle prioriteiten" },
  { value: "hoog",    label: "Hoog" },
  { value: "normaal", label: "Normaal" },
  { value: "laag",    label: "Laag" },
];

type ChipField = "status" | "priority" | "bemiddeling_status";

const toOptions = (labels: Record<string, string>, values: readonly string[] = Object.keys(labels)): ChipSelectOption[] =>
  values.map(value => ({ value, label: labels[value] ?? value }));
const STATUS_OPTIONS = toOptions(STATUS_LABELS);
const PRIORITY_OPTIONS = toOptions(PRIORITY_LABELS);
// A vacature still on a retired bemiddeling value (e.g. "shortlisted") keeps it
// listed, so the menu shows what it currently is.
function bemiddelingOptions(current: string): ChipSelectOption[] {
  const values: readonly string[] = BEMIDDELING_OPTIONS;
  return toOptions(BEMIDDELING_LABELS, values.includes(current) ? values : [...values, current]);
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
}

export default function Vacatures() {
  const { partnerId } = useParams<{ partnerId: string }>();
  const pid = Number(partnerId) || 0;

  const { user } = useAuth();
  const isPartnerBound = user?.role === "hmp" || user?.role === "partner_admin";
  const navigate = useNavigate();
  const { openSettings } = useSettingsDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeBranchId = searchParams.get("branch") ? Number(searchParams.get("branch")) : null;
  // kam/admin get extra tabs on /vacatures, kept in ?view= so they survive reloads and
  // back/forward: Partners (default) | Alle vacatures. /vacatures/:id shows the same
  // branch-tabbed table for every role.
  const view = searchParams.get("view");
  const tab: "partners" | "vacatures" = isPartnerBound || pid !== 0
    ? "vacatures"
    : (view === "vacatures" ? "vacatures" : "partners");
  function setTab(next: string) {
    setSearchParams(next === "partners" ? {} : { view: next }, { replace: true });
  }
  const branchAccessDenied = user?.role === "hmp"
    && activeBranchId !== null
    && !(user.branch_permissions ?? []).includes(activeBranchId);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const [filterStatus, setFilterStatus] = useState("alle");
  const [filterPrio, setFilterPrio] = useState("alle");
  const [page, setPage] = useState(1);
  // "Meerdere aanpassen" mode: checkboxes appear and a row click toggles its
  // selection instead of opening the vacature.
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  // Every vacature matching the current filters across all pages, fetched on
  // demand by "Alle … op alle pagina's selecteren"; while set, the selection
  // spans these instead of just the current page.
  const [allRows, setAllRows] = useState<Vacature[] | null>(null);
  const [loadingAll, setLoadingAll] = useState(false);

  // Filtering happens server-side, so it applies across every page (and to the total).
  const filters = {
    partner_id: pid,
    branch_id: activeBranchId ?? undefined,
    status: filterStatus !== "alle" ? filterStatus : undefined,
    priority: filterPrio !== "alle" ? filterPrio : undefined,
    q: debouncedSearch || undefined,
  };

  useEffect(() => { setPage(1); }, [pid, activeBranchId, filterStatus, filterPrio, debouncedSearch]);
  useEffect(() => { setSelected(new Set()); setAllRows(null); }, [pid, activeBranchId, page, tab, filterStatus, filterPrio, debouncedSearch]);
  useEffect(() => { setBulkMode(false); }, [pid, tab]);

  function setActiveBranchId(id: number | null) {
    const next: Record<string, string> = {};
    if (view) next.view = view;
    if (id !== null) next.branch = String(id);
    setSearchParams(next, { replace: true });
  }
  const [showModal, setShowModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [showMissingLocationModal, setShowMissingLocationModal] = useState(false);

  async function handleExport() {
    setExporting(true);
    setExportError(null);
    try {
      await exportVacatures(pid, { silenceErrors: true });
    } catch (e) {
      setExportError(e instanceof Error ? e.message : "Export mislukt");
    } finally {
      setExporting(false);
    }
  }

  const { data: vacResponse, isLoading: vacLoading, refetch: refetchVac } = useVacatures(
    { ...filters, page, limit: PAGE_SIZE },
    !branchAccessDenied && tab === "vacatures",
  );
  const { data: partners = [] } = usePartners();
  const partner = partners.find(p => p.id === pid);
  const { data: branches } = useBranches(pid, pid !== 0);
  const accessibleBranches = user?.role === "hmp"
    ? (branches ?? []).filter(b => (user.branch_permissions ?? []).includes(b.id))
    : (branches ?? []);
  const hasHiddenBranches = user?.role === "hmp" && accessibleBranches.length < (branches ?? []).length;

  const rows = vacResponse?.data ?? [];
  const totalCount = vacResponse?.total ?? 0;
  const hasMorePages = totalCount > rows.length;
  // The rows a selection may contain: the current page, or every page.
  const selectable = allRows ?? rows;

  const hasActiveFilters = filterStatus !== "alle" || filterPrio !== "alle" || !!search;
  function resetFilters() { setFilterStatus("alle"); setFilterPrio("alle"); setSearch(""); }

  const allVisibleSelected = rows.length > 0 && rows.every(v => selected.has(v.id));
  const allPagesSelected = allRows !== null && allRows.length > 0 && allRows.every(v => selected.has(v.id));
  function exitBulkMode() { setBulkMode(false); setSelected(new Set()); setAllRows(null); setShowBulkEdit(false); setShowBulkDelete(false); }
  async function selectAllPages() {
    setLoadingAll(true);
    try {
      const all = await listAllVacatures(filters);
      setAllRows(all);
      setSelected(new Set(all.map(v => v.id)));
    } finally {
      setLoadingAll(false);
    }
  }
  // After a partly failed bulk action, re-fetch the off-page rows so a retry previews current values.
  function refreshAllRows() {
    if (allRows) listAllVacatures(filters).then(setAllRows, () => {});
  }
  function toggleSelected(id: number) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // Saves a status/priority/bemiddeling change picked from a chip in the table;
  // the toast offers an undo back to the previous value. Like a bulk edit, it also
  // counts as "Bevestig actueel" (the backend stamps reviewed_at with its own clock);
  // the undo leaves that confirmation in place.
  async function updateChip(v: Vacature, field: ChipField, next: string) {
    const previous = v[field];
    try {
      await updateVacature(v.id, { [field]: next, reviewed_at: new Date().toISOString() }, { silenceErrors: true });
    } catch {
      toast(`"${v.functie}" bijwerken mislukt`, { kind: "error" });
      return;
    }
    await refetchVac();
    toast(`"${v.functie}" bijgewerkt`, {
      kind: "success",
      action: {
        label: "Ongedaan maken",
        onClick: async () => {
          try {
            await updateVacature(v.id, { [field]: previous }, { silenceErrors: true });
          } catch {
            toast("Ongedaan maken mislukt", { kind: "error" });
          }
          refetchVac();
        },
      },
    });
  }

  const columns: DataTableColumn<Vacature>[] = [
    { key: "functie", header: "Functie", width: "minmax(200px,1.4fr)", sortValue: v => v.functie, cell: v => <span className="font-bold">{v.functie}</span> },
    { key: "locatie", header: "Locatie", width: "minmax(100px,0.6fr)", sortValue: v => v.locatie || null, cell: v => <span className="truncate text-sm text-waves-body">{v.locatie || "—"}</span> },
    // In bulk mode a row click toggles selection, so the chips stay read-only there.
    { key: "status", header: "Status", width: "120px", sortValue: v => v.status, cell: v => bulkMode
      ? <StatusBadge value={v.status} />
      : <ChipSelect label="Status" value={v.status} options={STATUS_OPTIONS} pillKind={statusPillKind} onSelect={next => updateChip(v, "status", next)} /> },
    { key: "priority", header: "Prioriteit", width: "120px", sortValue: v => PRIO_ORDER[v.priority] ?? 99, cell: v => bulkMode
      ? <PriorityBadge value={v.priority} />
      : <ChipSelect label="Prioriteit" value={v.priority} options={PRIORITY_OPTIONS} pillKind={priorityPillKind} onSelect={next => updateChip(v, "priority", next)} /> },
    { key: "bemiddeling", header: "Bemiddeling", width: "150px", sortValue: v => v.bemiddeling_status, cell: v => bulkMode
      ? <BemiddelingBadge value={v.bemiddeling_status} />
      : <ChipSelect label="Bemiddeling" value={v.bemiddeling_status} options={bemiddelingOptions(v.bemiddeling_status)} pillKind={bemiddelingPillKind} onSelect={next => updateChip(v, "bemiddeling_status", next)} /> },
    { key: "hiring_manager", header: "Hiring manager", width: "150px", sortValue: v => v.hiring_manager || null, cell: v => <span className="truncate text-sm text-waves-body" title={v.hiring_manager || undefined}>{v.hiring_manager || "—"}</span> },
    { key: "updated_at", header: "Bijgewerkt", width: "100px", sortValue: v => v.updated_at ?? v.created_at, cell: v => <span className="font-mono text-sm text-waves-body">{shortDate(v.updated_at)}</span> },
  ];

  const actions = tab === "partners" ? undefined : (
    <>
      <Button variant="outline" data-tour="btn-export" onClick={handleExport} disabled={exporting} className="hidden sm:inline-flex">
        {exporting ? <Loader2 className="animate-spin" /> : <Download />}
        Export
      </Button>
      <Button data-tour="btn-new" onClick={() => setShowModal(true)}>
        <Plus /> <span className="hidden sm:inline">Nieuwe vacature</span>
      </Button>
    </>
  );

  const title = pid === 0 ? (tab === "partners" ? "Alle partners" : "Alle vacatures") : isPartnerBound ? "Vacatures" : partner?.name ?? "Vacatures";
  const subtitle = tab === "vacatures" ? (vacResponse ? `${totalCount} vacatures` : "Laden…") : "Partners met hun openstaande vacatures";

  return (
    <>
      <Layout title={title} subtitle={subtitle} actions={actions}>
        <div className="flex flex-col gap-5">
          {!isPartnerBound && pid === 0 && (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger value="partners">Partners</TabsTrigger>
                <TabsTrigger value="vacatures">Alle vacatures</TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {tab === "partners" && <PartnersOverview />}
          {tab === "vacatures" && <>
          {pid !== 0 && (
            <div className="flex items-end gap-4">
              <Tabs data-tour="vac-tabs" value={activeBranchId === null ? "all" : String(activeBranchId)} onValueChange={v => setActiveBranchId(v === "all" ? null : Number(v))} className="min-w-0 flex-1">
                <TabsList>
                  <TabsTrigger value="all">Alle vestigingen</TabsTrigger>
                  {accessibleBranches.map(b => <TabsTrigger key={b.id} value={String(b.id)}>{b.name}</TabsTrigger>)}
                </TabsList>
              </Tabs>
              {hasHiddenBranches && (
                <button
                  onClick={() => setShowMissingLocationModal(true)}
                  className="mb-3 flex shrink-0 items-center gap-1.5 text-[13px] font-bold text-waves-muted hover:text-waves-ink"
                >
                  <HelpCircle size={14} /> Mis je een locatie?
                </button>
              )}
            </div>
          )}

          {branchAccessDenied ? (
            <EmptyState
              icon={<Lock />}
              title="Geen toegang tot deze vestiging"
              description="Je hebt geen rechten om vacatures van deze vestiging in te zien. Vraag toegang aan via Instellingen."
              action={<Button variant="outline" onClick={() => openSettings("toegang")}>Naar instellingen</Button>}
              className="rounded-card border border-waves-line"
            />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <div data-tour="vac-search" className="flex h-10 w-full items-center gap-2 rounded-pill border border-waves-line bg-white px-4 sm:w-[260px]">
                  <Search size={15} className="shrink-0 text-waves-muted" />
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Zoek functie, locatie…"
                    className="min-w-0 flex-1 bg-transparent text-sm text-waves-ink outline-none placeholder:text-waves-muted"
                  />
                </div>
                <div data-tour="filter-status"><FilterDropdown aria-label="Status" value={filterStatus} onChange={setFilterStatus} options={STATUS_FILTER} /></div>
                <div data-tour="filter-prio"><FilterDropdown aria-label="Prioriteit" value={filterPrio} onChange={setFilterPrio} options={PRIO_FILTER} /></div>
                {hasActiveFilters && (
                  <button data-tour="vac-clear" onClick={resetFilters} className="flex items-center gap-1 px-1 text-[13px] font-bold text-waves-muted hover:text-waves-ink">
                    <X size={13} /> Wis filters
                  </button>
                )}
                {exportError && <span className="text-[13px] font-medium text-status-alert-fg">{exportError}</span>}
                <div className="ml-auto font-mono text-[13px] text-waves-muted">{totalCount} rijen</div>
                {!bulkMode && (
                  <Button variant="outline" data-tour="bulk-start" onClick={() => setBulkMode(true)}>
                    <ListChecks /> Meerdere aanpassen
                  </Button>
                )}
              </div>

              {bulkMode && (
                <BulkSelectBar
                  selectedCount={selected.size}
                  visibleCount={rows.length}
                  allSelected={allVisibleSelected}
                  onToggleAll={() => {
                    if (allVisibleSelected) { setSelected(new Set()); setAllRows(null); }
                    else setSelected(new Set(rows.map(v => v.id)));
                  }}
                  allPagesCount={hasMorePages ? totalCount : null}
                  allPagesSelected={allPagesSelected}
                  loadingAll={loadingAll}
                  onSelectAllPages={selectAllPages}
                  onCancel={exitBulkMode}
                  onEdit={() => setShowBulkEdit(true)}
                  onDelete={() => setShowBulkDelete(true)}
                />
              )}

              <DataTable
                columns={columns}
                rows={rows}
                rowKey={v => v.id}
                selectedKeys={bulkMode ? selected : undefined}
                onSelectionChange={bulkMode ? next => setSelected(next as Set<number>) : undefined}
                onRowClick={v => bulkMode ? toggleSelected(v.id) : navigate(`/vacature/${v.id}`)}
                minWidth={900}
                tour="vac-list"
                headerTour="vac-headers"
                rowTour={v => (isPracticeId(v.id) ? "vac-practice" : undefined)}
                empty={vacLoading ? "Vacatures laden…" : "Geen vacatures gevonden — pas de filters aan."}
              />

              {totalCount > PAGE_SIZE && (
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[13px] text-waves-muted">
                    {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} van {totalCount}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft /> Vorige
                    </Button>
                    <span className="px-2 font-mono text-[13px] text-waves-body">
                      {page} / {Math.ceil(totalCount / PAGE_SIZE)}
                    </span>
                    <Button variant="outline" size="sm" disabled={page * PAGE_SIZE >= totalCount} onClick={() => setPage(p => p + 1)}>
                      Volgende <ChevronRight />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
          </>}
        </div>
      </Layout>

      {showModal && (
        <NieuweVacatureModal
          partnerId={pid}
          defaultBranchId={activeBranchId}
          onClose={() => setShowModal(false)}
          onCreated={refetchVac}
        />
      )}

      {showBulkEdit && (
        <BulkEditModal
          selected={selectable.filter(v => selected.has(v.id))}
          onClose={() => setShowBulkEdit(false)}
          onDone={failed => {
            // Keep failed vacatures selected so they can be retried; leave bulk mode otherwise.
            if (failed.length === 0) exitBulkMode();
            else {
              setSelected(new Set(failed)); setShowBulkEdit(false);
              // Refresh the off-page rows too, so a retry previews their current values.
              refreshAllRows();
            }
            refetchVac();
          }}
          onUndone={refetchVac}
        />
      )}

      {showBulkDelete && (
        <BulkDeleteModal
          selected={selectable.filter(v => selected.has(v.id))}
          onClose={() => setShowBulkDelete(false)}
          onDone={failed => {
            if (failed.length === 0) exitBulkMode();
            else { setSelected(new Set(failed)); setShowBulkDelete(false); refreshAllRows(); }
            refetchVac();
          }}
        />
      )}

      <Dialog open={showMissingLocationModal} onOpenChange={setShowMissingLocationModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mis je een locatie?</DialogTitle>
            <DialogDescription>
              Je ziet mogelijk niet alle vestigingen van jouw organisatie. Vraag toegang aan via Instellingen.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMissingLocationModal(false)}>Sluiten</Button>
            <Button variant="ink" onClick={() => { setShowMissingLocationModal(false); openSettings("toegang"); }}>Naar instellingen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
